<template>
  <tutorial-layout title="VModel" :referenceLinks="referenceLinks" :youtubeLinks="youtubeLinks">

      <p slot="overview">
        As we learned in an earlier tutorial, the proper way to send data from a parent to a child component is through a prop.  
        We also know that props should not be modified within a child component (effectively making them read only at the child component level) .
        We can change the value of a prop indirectly by emitting an event with the new value up to the parent component.
        When configured correctly, the parent component can handle the event and proceed to modify its local version of the prop variable triggering
        a reactive cascade back down to the child component.
      </p>

      <p slot="benefits">
        The benefits of learning this concept are cleaner code as well as an inherent understanding of how the underlying component is handling the I/O of the variables.
        <br />
        Also, since most of the standard form controls <b>&lt;input ... /&gt;</b> as well as Vuetify controls are already wired up to use the v-model convention you will be well prepared to use and understand form control I/O.
      </p>
      




      <p>
        The v-model convention is an extension of this concept.
        It makes it so that when the child prop and event is named appropriately you can use the v-model shorthand instead to accomplish a two-way binding.
        
      </p>

      Take the following code as an example of how to write the I/O contract between the parent and child component.
      <code>
        &lt;input :value="myValue" @input="myValue=$event" &gt;
      </code>

      <p>
        The above code is equivalent to:
        <br />
        <code>
          &lt;input v-model="myValue" &gt;
        </code>
        <br />
      </p>



      <h2>Examples of v-model usage</h2>
      <v-card>
        <v-card-text>
          <div>
            <h3>Vuetify Text Field With Explicit Two-way Binding</h3>
            <v-text-field solo-inverted :value="text1" @input="text1 = $event" label="Text1" />
            <code>
              &lt;v-text-field ... :value="text1" @input="text1 = $event" /&gt;
            </code>

          </div>
        </v-card-text>
      </v-card>
      <br />
      <v-card>
        <v-card-text>
          <div>
            <h3>Vuetify Text Field With V-Model</h3>
            <v-text-field solo-inverted v-model="text1" label="Text1" />
            <code>
              &lt;v-text-field ... v-model="text1" /&gt;
            </code>
          </div>

        </v-card-text>
      </v-card>
      <br />
      <v-card>
        <v-card-text>

          <div>
            <h3>The variable is now bound, so it can be used anywhere</h3>
            <p>Such as an old-school html marquee</p>
            <marquee>{{text1}}</marquee>
          </div>

        </v-card-text>
      </v-card>
      <br />
      <v-card>
        <v-card-text>

          <div>
            <h3>The Power Comes from creating our own components.</h3>
            <p>
              Here is a simple example that can show you how it could work in your own controls.  Click on the buttons below to change the value within the component and watch as the binding propagates throughout the page.
              <br />
              <v-model-example v-model="text1" />

              <br />

              <p>
                This gives us the ability to handle a variety of scenarios with ease.
                Anytime there is an abstraction between what the parent component knows and what the user understands; you can write a custom input to handle the translation.
              </p>
              Here are some examples where this would be useful:
              <br />
              <ul>
                <li>Dates or colors</li>
                <li>Formatted text such as email address</li>
                <li>As well as HyperLinks</li>
                <li>Custom Lists</li>
                <li>more...</li>
              </ul>

            </p>
          </div>

        </v-card-text>
      </v-card>

<!-- 
      <v-card>
        <v-card-title>References</v-card-title>

        <div v-for="(link,l) in referenceLinks" :key="`ref-link-${l}`">

          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>{{link.title}}</v-list-item-title>
              <v-list-item-subtitle><a :href="link.href" target="_blank">{{link.href}}</a></v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action><v-btn :href="link.href" target="_blank" color="primary">Go</v-btn></v-list-item-action>
          </v-list-item>

          <v-divider />

        </div>


      </v-card> -->

  </tutorial-layout>

</template>

<script>
import moment from "moment";

import uiView from "@/_mixins/ui-view-mixin";
import vModelExample from "./v-model-example";
import tutorialLayout from "../../components/tutorial-layout";
export default {
  name: "VModel",
  mixins: [uiView],
  created() {},
  data() {
    return {
      text1: "Example Text",
    };
  },
  components: { tutorialLayout, vModelExample },
  props: {},
  watch: {},
  computed: {
    referenceLinks() {
      return [
        {
          title: "Vue.Js v-model Example Tutorial",
          href:
            "https://www.nicesnippets.com/blog/vuejs-v-model-example-tutorial",
        },
        {
          title: "v-model overview (vuejys.org)",
          href: "https://v3.vuejs.org/guide/migration/v-model.html",
        },
        {
          title: "Vue.js Array v-model Use",
          href:
            "https://courses.bekwam.net/public_tutorials/bkcourse_vuejs_v-model_array.html",
        },
      ];
    },
    youtubeLinks() {
      return [
        {
          title: "2 way binding",
          href: "https://www.youtube.com/embed/nEdsu6heW9o",
        },
        {
          title: "Data binding with V-Model",
          href: "https://www.youtube.com/embed/9rmfaA-ndak",
        },
        {
          title: "V-Modal dynamic data",
          href: "https://www.youtube.com/embed/ag_cqFHV0p0",
        },
      ];
    },
  },
  methods: {},
};
</script>
<style lang="scss">
</style>
